import React, { useState } from 'react';
import {
  Search,
  RefreshCw,
  Eye,
  Trash2,
  Star,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  X,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { ConfirmModal } from '@/components/ui/Modal';
import { useFeedbacks, useDeleteFeedback } from '@/hooks/useFeedbacks';
import type { FeedbackFilters, FeedbackType, Feedback } from '@/types/feedback';
import { FEEDBACK_TYPE_LABELS, FEEDBACK_TYPE_COLORS } from '@/types/feedback';

export const FeedbacksListPage: React.FC = () => {
  const [filters, setFilters] = useState<FeedbackFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data, isLoading, refetch, isFetching } = useFeedbacks(filters);
  const deleteMutation = useDeleteFeedback();

  const feedbacks = data?.feedbacks ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;

  const handleFilterChange = (key: keyof FeedbackFilters, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset page to 1 only when changing filters, not when changing page itself
      ...(key !== 'page' && { page: 1 }),
    }));
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      await deleteMutation.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
      setSelectedFeedback(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStars = (rating?: number) => {
    if (!rating) return <span className="text-gray-400">-</span>;
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Filter feedbacks by search query
  const filteredFeedbacks = feedbacks.filter((fb) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      fb.message.toLowerCase().includes(query) ||
      fb.name?.toLowerCase().includes(query) ||
      fb.email?.toLowerCase().includes(query) ||
      fb.phoneNumber?.includes(query)
    );
  });

  if (isLoading) {
    return <PageLoader message="Carregando feedbacks..." />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Feedbacks
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Veja o que os usuarios estao dizendo
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          isLoading={isFetching}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Atualizar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por mensagem, nome, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Type filter */}
            <select
              value={filters.type || ''}
              onChange={(e) =>
                handleFilterChange(
                  'type',
                  e.target.value ? (e.target.value as FeedbackType) : undefined
                )
              }
              className="input w-full md:w-48"
            >
              <option value="">Todos os tipos</option>
              <option value="general">Geral</option>
              <option value="feature_request">Sugestao</option>
              <option value="bug">Bug/Erro</option>
              <option value="complaint">Reclamacao</option>
              <option value="praise">Elogio</option>
              <option value="account_deletion">Excluir conta</option>
            </select>

            {/* Sort */}
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters((prev) => ({
                  ...prev,
                  sortBy: sortBy as 'createdAt' | 'rating',
                  sortOrder: sortOrder as 'asc' | 'desc',
                }));
              }}
              className="input w-full md:w-48"
            >
              <option value="createdAt-desc">Mais recentes</option>
              <option value="createdAt-asc">Mais antigos</option>
              <option value="rating-desc">Maior avaliacao</option>
              <option value="rating-asc">Menor avaliacao</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Feedbacks table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-secondary-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Tipo
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Mensagem
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Avaliacao
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Contato
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Data
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-secondary-700">
              {filteredFeedbacks.map((feedback) => (
                <tr
                  key={feedback.id}
                  className="hover:bg-gray-50 dark:hover:bg-secondary-700/50"
                >
                  <td className="px-4 py-3">
                    <Badge className={FEEDBACK_TYPE_COLORS[feedback.type]}>
                      {FEEDBACK_TYPE_LABELS[feedback.type]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                      {feedback.message}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {renderStars(feedback.rating)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      {feedback.name && (
                        <p className="font-medium text-gray-900 dark:text-white">
                          {feedback.name}
                        </p>
                      )}
                      {feedback.email && (
                        <p className="text-gray-500 dark:text-gray-400">
                          {feedback.email}
                        </p>
                      )}
                      {feedback.phoneNumber && (
                        <p className="text-gray-500 dark:text-gray-400">
                          {feedback.phoneNumber}
                        </p>
                      )}
                      {!feedback.name && !feedback.email && !feedback.phoneNumber && (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(feedback.createdAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedFeedback(feedback)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(feedback.id)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty state */}
          {filteredFeedbacks.length === 0 && (
            <div className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                Nenhum feedback encontrado
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery || filters.type
                  ? 'Tente ajustar os filtros.'
                  : 'Ainda nao ha feedbacks dos usuarios.'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredFeedbacks.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-secondary-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Mostrando {filteredFeedbacks.length} de {total} feedbacks
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={(filters.page || 1) <= 1}
                onClick={() =>
                  handleFilterChange('page', (filters.page || 1) - 1)
                }
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Pagina {filters.page || 1} de {totalPages || 1}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={(filters.page || 1) >= totalPages}
                onClick={() =>
                  handleFilterChange('page', (filters.page || 1) + 1)
                }
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Feedback detail modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader
              action={
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              }
            >
              <CardTitle>Detalhes do Feedback</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tipo
                    </p>
                    <Badge className={FEEDBACK_TYPE_COLORS[selectedFeedback.type]}>
                      {FEEDBACK_TYPE_LABELS[selectedFeedback.type]}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Avaliacao
                    </p>
                    {renderStars(selectedFeedback.rating)}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Data
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(selectedFeedback.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ID
                    </p>
                    <p className="font-mono text-sm text-gray-900 dark:text-white">
                      {selectedFeedback.id}
                    </p>
                  </div>
                </div>

                {/* Contact info */}
                {(selectedFeedback.name || selectedFeedback.email || selectedFeedback.phoneNumber) && (
                  <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Informacoes de Contato
                    </p>
                    {selectedFeedback.name && (
                      <p className="text-gray-900 dark:text-white">
                        <span className="text-gray-500 dark:text-gray-400">Nome:</span> {selectedFeedback.name}
                      </p>
                    )}
                    {selectedFeedback.email && (
                      <p className="text-gray-900 dark:text-white">
                        <span className="text-gray-500 dark:text-gray-400">Email:</span>{' '}
                        <a href={`mailto:${selectedFeedback.email}`} className="text-primary-600 hover:underline">
                          {selectedFeedback.email}
                        </a>
                      </p>
                    )}
                    {selectedFeedback.phoneNumber && (
                      <p className="text-gray-900 dark:text-white">
                        <span className="text-gray-500 dark:text-gray-400">Telefone:</span>{' '}
                        <a href={`https://wa.me/${selectedFeedback.phoneNumber}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          {selectedFeedback.phoneNumber}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Mensagem
                  </p>
                  <p className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedFeedback.message}
                  </p>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-secondary-700">
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteClick(selectedFeedback.id)}
                    isLoading={deleteMutation.isPending}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Excluir
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedFeedback(null)}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title="Confirmar exclusao"
        message="Tem certeza que deseja excluir este feedback?"
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default FeedbacksListPage;
